// Tracks whether the viewport is narrow enough to want the phone layout — the
// one-screen-at-a-time drill-down tree — rather than the desktop's two panes of
// panels shown all at once. A hook rather than pure CSS because the two layouts
// are genuinely different DOM (a stack of foldable panels vs. a navigable tree),
// so the choice has to reach React, not just the stylesheet.

import { useEffect, useState } from "react";

/** The breakpoint below which the playground switches to the mobile tree. Kept
 *  here (not in CSS) because it is React that swaps the layout. */
export const MOBILE_MAX_WIDTH = 720;

const QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;

export function useIsMobile(): boolean {
  // SSR-safe and old-Safari-safe: `matchMedia` may be absent, and its
  // `addEventListener` form is newer than its deprecated `addListener`.
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined" && "matchMedia" in window
      ? window.matchMedia(QUERY).matches
      : false
  );

  useEffect(() => {
    if (typeof window === "undefined" || !("matchMedia" in window)) return;
    const mql = window.matchMedia(QUERY);
    const onChange = (): void => setIsMobile(mql.matches);
    onChange();
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return isMobile;
}
